import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import React from 'react';
import i18n from '../../i18n';
import { ReactComponent as DocumentIcon } from '../../static/images/svg/archive-document.svg';
import { ReactComponent as ManualEntryIcon } from '../../static/images/svg/archive-manual-entry.svg';
import DialogComponent from '../dialog/dialog.component';
import './archive-create-mode-dialog.scss';
import './archive-form.scss';
import GlobalNotification from '../common/snackbar';
import { validateAnagraficaOcrFile } from './anagrafica-ocr';
import { ArchiveOcrParsingDialog } from './archive-ocr-parsing-dialog.component';

/** 建档主体类型，用于选择标题与主体文案。 */
export type ArchiveCreateEntityType = 'group' | 'restaurant';

/** 新增方式弹窗参数。 */
export type ArchiveCreateModeDialogProps = {
    /** 是否显示弹窗。 */
    open: boolean;
    /** 当前新增的业务主体。 */
    entityType: ArchiveCreateEntityType;
    /** 餐厅所属集团名称；集团模式不使用。 */
    groupName?: string;
    /** 关闭弹窗。 */
    onClose: () => void;
    /** 进入现有手动新增表单。 */
    onManualEntry: () => void;
    /** 餐厅扫描选定合法文件后交给父级上传。集团不使用。 */
    onScanFile?: (file: File) => Promise<void> | void;
};

/**
 * 展示扫描登记文件或手动填写的建档方式选择。
 * @param props 弹窗状态、业务主体与操作回调
 * @returns 新增方式选择弹窗
 */
export function ArchiveCreateModeDialog({
    open,
    entityType,
    groupName,
    onClose,
    onManualEntry,
    onScanFile,
}: ArchiveCreateModeDialogProps): JSX.Element {
    /** 是否为餐厅新增场景。 */
    const isRestaurant = entityType === 'restaurant';
    /** 当前业务主体对应的弹窗标题。 */
    const titleKey = isRestaurant
        ? 'archive_form_create_restaurant_title'
        : 'archive_form_create_group_title';
    /** 当前业务主体对应的登记文件说明。 */
    const documentDescriptionKey = isRestaurant
        ? 'archive_form_restaurant_document_description'
        : 'archive_form_group_document_description';
    /** 隐藏的文件选择器，仅餐厅扫描使用。 */
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    /** 是否正在等待识别接口（同时控制解析弹窗）。 */
    const [parsing, setParsing] = React.useState(false);
    /** 当前识别中的文件名，作解析弹窗标题。 */
    const [parsingFileName, setParsingFileName] = React.useState('');

    React.useEffect(() => {
        if (!open) {
            setParsing(false);
            setParsingFileName('');
        }
    }, [open]);

    /**
     * 校验文件后交给父级上传，并打开识别进度弹窗。
     * @param file 用户选择或拖入的文件
     */
    const handleScanFile = async (file: File) => {
        const check = validateAnagraficaOcrFile(file);
        if (check === 'type') {
            GlobalNotification.error(i18n.t('archive_form_ocr_file_type'));
            return;
        }
        if (check === 'size') {
            GlobalNotification.error(i18n.t('archive_form_ocr_file_size'));
            return;
        }
        setParsingFileName(file.name);
        setParsing(true);
        try {
            await onScanFile?.(file);
        } finally {
            setParsing(false);
            setParsingFileName('');
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    return (
        <>
        <DialogComponent
            open={open}
            maxWidth='sm'
            defaultHeaderProps={{ title: i18n.t(titleKey) }}
            onClose={onClose}
            footer={<></>}
            sx={{
                '& .MuiDialog-container > .MuiPaper-root': {
                    maxWidth: '620px',
                    border: 0,
                    borderRadius: '12px',
                    background: 'var(--fill-neutral-100)',
                },
                '& .dialog-footer': {
                    display: 'none',
                },
            }}
            className='archive-create-mode-dialog'
        >
            <Stack spacing={3}>
                {isRestaurant && (
                    <Typography variant='body2' color='text.secondary'>
                        {i18n.t('archive_form_restaurant_group_hint', { groupName })}
                    </Typography>
                )}

                <ButtonBase
                    className='archive-create-mode-choice'
                    aria-label={isRestaurant
                        ? i18n.t('archive_form_scan_document')
                        : `${i18n.t('archive_form_scan_document')} ${i18n.t('archive_form_ocr_unavailable')}`}
                    onClick={() => {
                        if (!isRestaurant) {
                            GlobalNotification.error(i18n.t('archive_form_ocr_unavailable'));
                            return;
                        }
                        fileInputRef.current?.click();
                    }}
                    onDragOver={(event) => {
                        if (isRestaurant) {
                            event.preventDefault();
                        }
                    }}
                    onDrop={(event) => {
                        if (!isRestaurant) {
                            return;
                        }
                        event.preventDefault();
                        const file = event.dataTransfer.files?.[0];
                        if (file) {
                            void handleScanFile(file);
                        }
                    }}
                >
                    <DocumentIcon className='archive-create-mode-icon' aria-hidden='true' />
                    <Box className='archive-create-mode-content'>
                        <Typography variant='subtitle2'>
                            {i18n.t('archive_form_scan_document')}
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                            {i18n.t(documentDescriptionKey)}
                        </Typography>
                        <Box className='archive-create-mode-meta'>
                            <Typography variant='caption' className='archive-create-mode-badge'>
                                {i18n.t('archive_form_scan_coverage')}
                            </Typography>
                            <Typography variant='caption' className='archive-create-mode-drop'>
                                {i18n.t('archive_form_drop_document')}
                            </Typography>
                        </Box>
                    </Box>
                </ButtonBase>
                {isRestaurant ? (
                    <input
                        ref={fileInputRef}
                        className="archive-create-mode-file-input"
                        data-testid="archive-ocr-file-input"
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,application/pdf,image/jpeg,image/png,image/heic,image/heif"
                        onChange={(event) => {
                            const file = event.target.files?.[0];
                            if (file) {
                                void handleScanFile(file);
                            }
                        }}
                    />
                ) : null}

                <ButtonBase
                    className='archive-create-mode-choice'
                    aria-label={i18n.t('archive_form_manual_entry')}
                    onClick={onManualEntry}
                >
                    <ManualEntryIcon className='archive-create-mode-icon' aria-hidden='true' />
                    <Box className='archive-create-mode-content'>
                        <Typography variant='subtitle2'>
                            {i18n.t('archive_form_manual_entry')}
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                            {i18n.t('archive_form_manual_entry_description')}
                        </Typography>
                    </Box>
                </ButtonBase>
            </Stack>
        </DialogComponent>
        <ArchiveOcrParsingDialog open={parsing} fileName={parsingFileName} />
        </>
    );
}
